/**
 * SMART DISCOVERY
 * 
 * A cinematic, step-by-step guided experience where Athena
 * discovers your competitors in real-time.
 * 
 * The experience should feel like watching a master strategist
 * uncover intelligence on your battlefield.
 */

import React, { useState, useEffect, useRef } from 'react';

// Radar Scanner Animation
const RadarScanner = ({ isScanning, discoveredCount }) => {
  return (
    <div className="relative w-64 h-64 mx-auto">
      {/* Outer rings */}
      {[1, 2, 3].map(ring => (
        <div 
          key={ring}
          className={`
            absolute inset-0 rounded-full border
            ${ring === 1 ? 'border-cyan-500/30' : ring === 2 ? 'border-cyan-500/20' : 'border-cyan-500/10'}
          `}
          style={{ 
            transform: `scale(${ring * 0.33})`,
            animation: isScanning ? `pulse ${ring}s ease-in-out infinite` : 'none'
          }}
        />
      ))}
      
      {/* Scanning line */}
      {isScanning && (
        <div 
          className="absolute inset-0 origin-center"
          style={{ animation: 'spin 2s linear infinite' }}
        >
          <div 
            className="absolute top-1/2 left-1/2 w-1/2 h-0.5 origin-left"
            style={{
              background: 'linear-gradient(90deg, rgba(0,212,255,0.8) 0%, transparent 100%)',
              boxShadow: '0 0 10px rgba(0,212,255,0.5)'
            }}
          />
        </div>
      )}
      
      {/* Center dot */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
        <div className={`
          w-4 h-4 rounded-full
          ${isScanning ? 'bg-cyan-400 animate-pulse' : 'bg-cyan-500/50'}
          shadow-lg shadow-cyan-400/50
        `} />
      </div>
      
      {/* Discovered blips */}
      {Array.from({ length: discoveredCount }).map((_, i) => {
        const angle = (i / Math.max(discoveredCount, 1)) * Math.PI * 2;
        // Use deterministic distance based on index instead of Math.random()
        const distance = 40 + ((i * 17) % 40);
        const x = Math.cos(angle) * distance;
        const y = Math.sin(angle) * distance;
        
        return (
          <div
            key={i}
            className="absolute w-3 h-3 bg-amber-500 rounded-full animate-ping"
            style={{
              top: `calc(50% + ${y}px)`,
              left: `calc(50% + ${x}px)`,
              transform: 'translate(-50%, -50%)',
              animationDelay: `${i * 200}ms`,
              animationDuration: '1.5s'
            }}
          />
        );
      })}
      
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 0.3; transform: scale(var(--scale, 1)); }
          50% { opacity: 0.6; transform: scale(calc(var(--scale, 1) * 1.02)); }
        }
      `}</style>
    </div>
  );
};

// Athena's Commentary
const AthenaCommentary = ({ messages }) => {
  const containerRef = useRef(null);
  
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [messages]);
  
  return (
    <div 
      ref={containerRef}
      className="h-48 overflow-y-auto space-y-2 pr-2 scrollbar-thin scrollbar-thumb-gray-700"
    >
      {messages.map((msg, i) => (
        <div 
          key={i}
          className={`
            flex items-start gap-3 p-3 rounded-lg
            ${msg.type === 'insight' ? 'bg-amber-500/10 border border-amber-500/20' :
              msg.type === 'discovery' ? 'bg-emerald-500/10 border border-emerald-500/20' :
              msg.type === 'warning' ? 'bg-red-500/10 border border-red-500/20' :
              'bg-gray-800/50'}
            animate-fadeIn
          `}
          style={{ animationDelay: `${i * 50}ms` }}
        >
          <span className="text-lg">
            {msg.type === 'insight' ? '🦉' :
             msg.type === 'discovery' ? '🎯' :
             msg.type === 'warning' ? '⚠️' : '💭'}
          </span>
          <div>
            <p className="text-sm text-gray-300">{msg.text}</p>
            {msg.detail && <p className="text-xs text-gray-500 mt-1">{msg.detail}</p>}
          </div>
        </div>
      ))}
      
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

// Discovered Competitor Card (animated reveal)
const DiscoveredCompetitorCard = ({ competitor, index, onSelect, isSelected }) => {
  return (
    <div 
      className={`
        relative p-4 rounded-xl border-2 cursor-pointer
        transition-all duration-300 transform
        ${isSelected 
          ? 'border-cyan-500 bg-cyan-500/10 scale-[1.02]' 
          : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'}
      `}
      onClick={() => onSelect(competitor)}
      style={{ 
        animation: 'slideIn 0.5s ease-out forwards',
        animationDelay: `${index * 150}ms`,
        opacity: 0
      }}
    >
      {isSelected && (
        <div className="absolute top-2 right-2 w-5 h-5 bg-cyan-500 rounded-full flex items-center justify-center">
          <span className="text-xs">✓</span>
        </div>
      )}
      
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-gray-700 to-gray-800 flex items-center justify-center">
          <span className="text-lg">🏢</span>
        </div>
        <div>
          <h4 className="font-semibold text-white">{competitor.name}</h4>
          <p className="text-xs text-gray-500">{competitor.domain}</p>
        </div>
      </div>
      
      {competitor.relevance_score && (
        <div className="mt-3 flex items-center justify-between">
          <span className="text-xs text-gray-500">Relevance</span>
          <div className="flex items-center gap-2">
            <div className="w-20 h-1.5 bg-gray-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-cyan-500 to-purple-500 rounded-full"
                style={{ width: `${competitor.relevance_score}%` }}
              />
            </div>
            <span className="text-xs text-cyan-400 font-medium">{competitor.relevance_score}%</span>
          </div>
        </div>
      )}
      
      <style>{`
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(-20px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
};

// Main Smart Discovery Component
const SmartDiscovery = ({ API, onComplete, onCancel }) => {
  const [step, setStep] = useState(1);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [keywords, setKeywords] = useState([]);
  const [keywordInput, setKeywordInput] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [discoveredCompetitors, setDiscoveredCompetitors] = useState([]);
  const [selectedCompetitors, setSelectedCompetitors] = useState([]);
  const [athenaMessages, setAthenaMessages] = useState([]);
  const [error, setError] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  
  // Simulated Athena messages during analysis
  const addAthenaMessage = (type, text, detail = null) => {
    setAthenaMessages(prev => [...prev, { type, text, detail }]);
  };
  
  const handleAddKeyword = () => {
    if (keywordInput.trim() && keywords.length < 5) {
      setKeywords(prev => [...prev, keywordInput.trim()]);
      setKeywordInput('');
    }
  };
  
  const handleRemoveKeyword = (kw) => {
    setKeywords(prev => prev.filter(k => k !== kw));
  };
  
  const toggleCompetitor = (competitor) => {
    setSelectedCompetitors(prev => {
      const isSelected = prev.some(c => c.domain === competitor.domain);
      if (isSelected) {
        return prev.filter(c => c.domain !== competitor.domain);
      } else {
        return [...prev, competitor];
      }
    });
  };
  
  const runDiscovery = async () => {
    setIsAnalyzing(true);
    setAthenaMessages([]);
    setError(null);
    
    addAthenaMessage('thought', 'Initiating reconnaissance protocol...');
    
    try {
      const token = localStorage.getItem('token');
      
      // Simulate progressive discovery with messages
      await new Promise(r => setTimeout(r, 1000));
      addAthenaMessage('insight', `Scanning ${websiteUrl}...`, 'Analyzing your digital footprint');
      
      await new Promise(r => setTimeout(r, 1500));
      addAthenaMessage('thought', 'Identifying your market niche and primary keywords...');
      
      // Make actual API call
      const response = await fetch(`${API}/agents/smart-discovery`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          website_url: websiteUrl,
          industry_hint: keywords.join(', '),
          max_competitors: 10
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Discovery failed');
      }
      
      // Process results with dramatic reveal
      const competitors = data.discovery?.competitors || [];
      
      addAthenaMessage('discovery', `Found ${competitors.length} potential competitors!`);
      
      await new Promise(r => setTimeout(r, 500));
      
      // Add competitors one by one for dramatic effect
      for (let i = 0; i < competitors.length; i++) {
        await new Promise(r => setTimeout(r, 300));
        setDiscoveredCompetitors(prev => [...prev, competitors[i]]);
        addAthenaMessage('discovery', `Identified: ${competitors[i].name}`, competitors[i].domain);
      }
      
      addAthenaMessage('insight', 'Analysis complete. Select the competitors you want me to track.');
      setAnalysisResult(data.discovery);
      setStep(3);
      
    } catch (err) {
      setError(err.message);
      addAthenaMessage('warning', 'Reconnaissance failed', err.message);
    } finally {
      setIsAnalyzing(false);
    }
  };
  
  const runEnhancedAnalysis = async () => {
    if (selectedCompetitors.length === 0) return;
    
    setIsAnalyzing(true);
    addAthenaMessage('thought', 'Initiating deep reconnaissance on selected targets...');
    
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API}/agents/enhanced-analysis`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          website_url: websiteUrl,
          target_keywords: keywords,
          max_competitors: selectedCompetitors.length,
          deep_crawl_top: Math.min(selectedCompetitors.length, 3)
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Enhanced analysis failed');
      }
      
      addAthenaMessage('insight', 'Deep analysis complete! Strategic intelligence gathered.');
      
      // Complete with full results
      onComplete({
        ...data.analysis,
        competitors: selectedCompetitors,
        keywords
      });
      
    } catch (err) {
      setError(err.message);
      addAthenaMessage('warning', 'Deep analysis encountered an obstacle', err.message);
    } finally {
      setIsAnalyzing(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-xl z-50 flex items-start sm:items-center justify-center p-4 py-6 sm:py-4 overflow-y-auto" data-testid="smart-discovery-overlay">
      <div className="w-full max-w-4xl bg-gradient-to-br from-gray-900 to-gray-950 rounded-2xl border border-gray-800 overflow-hidden shadow-2xl max-h-[90vh] overflow-y-auto my-auto" data-testid="smart-discovery-modal">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-800 bg-gradient-to-r from-amber-500/10 to-transparent">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                <span className="text-xl">🦉</span>
              </div>
              <div>
                <h2 className="font-bold text-white">Smart Discovery</h2>
                <p className="text-xs text-gray-500">Athena&apos;s Competitive Intelligence</p>
              </div>
            </div>
            <button 
              onClick={onCancel}
              className="text-gray-500 hover:text-white transition-colors"
            >
              ✕
            </button>
          </div>
          
          {/* Progress Steps */}
          <div className="flex items-center gap-2 mt-4">
            {['Your Website', 'Discovery', 'Select & Analyze'].map((label, i) => (
              <React.Fragment key={i}>
                <div className={`
                  flex items-center gap-2 px-3 py-1.5 rounded-full text-sm
                  ${step > i + 1 ? 'bg-emerald-500/20 text-emerald-400' :
                    step === i + 1 ? 'bg-cyan-500/20 text-cyan-400' :
                    'bg-gray-800 text-gray-500'}
                `}>
                  <span className={`
                    w-5 h-5 rounded-full flex items-center justify-center text-xs
                    ${step > i + 1 ? 'bg-emerald-500' :
                      step === i + 1 ? 'bg-cyan-500' : 'bg-gray-700'}
                  `}>
                    {step > i + 1 ? '✓' : i + 1}
                  </span>
                  {label}
                </div>
                {i < 2 && <div className={`flex-1 h-px ${step > i + 1 ? 'bg-emerald-500' : 'bg-gray-800'}`} />}
              </React.Fragment>
            ))}
          </div>
        </div>
        
        {/* Content */}
        <div className="p-6">
          {error && (
            <div className="mb-4 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400">
              {error}
            </div>
          )}
          
          {/* Step 1: Enter Website */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-white mb-2">Enter Your Battlefield</h3>
                <p className="text-gray-400">Tell Athena about your territory so she can identify your competitors</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">Your Website URL</label>
                <input
                  type="url"
                  value={websiteUrl}
                  onChange={(e) => setWebsiteUrl(e.target.value)}
                  placeholder="https://yourcompany.com"
                  className="
                    w-full px-4 py-3 rounded-xl
                    bg-gray-800/50 border border-gray-700
                    text-white placeholder-gray-500
                    focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500
                    transition-all duration-300
                  "
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Target Keywords <span className="text-gray-600">(optional, up to 5)</span>
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={keywordInput}
                    onChange={(e) => setKeywordInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddKeyword()}
                    placeholder="e.g., AI marketing, content automation"
                    className="
                      flex-1 px-4 py-3 rounded-xl
                      bg-gray-800/50 border border-gray-700
                      text-white placeholder-gray-500
                      focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500
                      transition-all duration-300
                    "
                  />
                  <button
                    onClick={handleAddKeyword}
                    disabled={keywords.length >= 5}
                    className="
                      px-4 py-3 rounded-xl bg-gray-800 text-white
                      hover:bg-gray-700 transition-colors
                      disabled:opacity-50 disabled:cursor-not-allowed
                    "
                  >
                    Add
                  </button>
                </div>
                
                {keywords.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {keywords.map((kw, i) => (
                      <span 
                        key={i}
                        className="
                          inline-flex items-center gap-1 px-3 py-1
                          bg-cyan-500/20 text-cyan-400 rounded-full text-sm
                        "
                      >
                        {kw}
                        <button 
                          onClick={() => handleRemoveKeyword(kw)}
                          className="hover:text-cyan-200"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Step 2: Discovery in Progress */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="text-2xl font-bold text-white mb-2">Athena is Scanning...</h3>
                <p className="text-gray-400">Deploying reconnaissance protocols</p>
              </div>
              
              <RadarScanner isScanning={isAnalyzing} discoveredCount={discoveredCompetitors.length} />
              
              <AthenaCommentary messages={athenaMessages} />
            </div>
          )}
          
          {/* Step 3: Select Competitors */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="text-center mb-4">
                <h3 className="text-2xl font-bold text-white mb-2">Targets Identified</h3>
                <p className="text-gray-400">Select competitors for deep analysis</p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-3 max-h-[400px] overflow-y-auto pr-2">
                {discoveredCompetitors.map((comp, i) => (
                  <DiscoveredCompetitorCard
                    key={comp.domain}
                    competitor={comp}
                    index={i}
                    onSelect={toggleCompetitor}
                    isSelected={selectedCompetitors.some(c => c.domain === comp.domain)}
                  />
                ))}
              </div>
              
              <div className="flex items-center justify-between pt-4 border-t border-gray-800">
                <p className="text-sm text-gray-500">
                  {selectedCompetitors.length} competitor{selectedCompetitors.length !== 1 ? 's' : ''} selected
                </p>
                <p className="text-sm text-amber-400">
                  ~30 credits for deep analysis
                </p>
              </div>
              
              <AthenaCommentary messages={athenaMessages} />
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-800 bg-gray-900/50 flex justify-between">
          <button
            onClick={() => step > 1 ? setStep(step - 1) : onCancel()}
            className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
          >
            {step > 1 ? '← Back' : 'Cancel'}
          </button>
          
          {step === 1 && (
            <button
              onClick={() => { setStep(2); runDiscovery(); }}
              disabled={!websiteUrl}
              className="
                px-6 py-2.5 rounded-xl font-semibold
                bg-gradient-to-r from-cyan-500 to-purple-600
                hover:from-cyan-400 hover:to-purple-500
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-all duration-300
              "
            >
              🔍 Start Discovery
            </button>
          )}
          
          {step === 3 && (
            <button
              onClick={runEnhancedAnalysis}
              disabled={selectedCompetitors.length === 0 || isAnalyzing}
              className="
                px-6 py-2.5 rounded-xl font-semibold
                bg-gradient-to-r from-amber-500 to-orange-600
                hover:from-amber-400 hover:to-orange-500
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-all duration-300
              "
            >
              {isAnalyzing ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin">⏳</span>
                  Analyzing...
                </span>
              ) : (
                '🦉 Launch Deep Analysis'
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SmartDiscovery;
